import React, { useCallback, type JSX } from 'react';

import {
  updateDuplicateIdAction,
  updateRuleAction,
  updateStageAction,
} from 'app-components/flux/transaction-rules/transactionRuleActions';
import { useManager } from 'app-components/flux/transaction-rules/transactionRulesFormHooks';
import type { ContextType } from 'app-components/flux/transaction-rules/transactionRulesTypes';
import { ConditionsEnum } from 'app-components/flux/transaction-rules/transactionRulesTypes';
import type { RemoteTransactionRuleType } from 'app-components/flux/transaction-rules/transactionRulesTypes';
import { EDITING, ResolutionTypeEnum, VALIDATED } from 'app-components/flux/transaction-rules/transactionRulesTypes';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';

import { useStateContext } from 'app-components/flux/transaction-rules/transactionRulesFormState';

import { RuleDuplciateDialog } from './RuleDuplicateDialog';
import { SourceHeading, TargetHeading } from './reviewConditions/Headings';
import { PayeeSourceCard } from './sourceConditions/PayeeSourceCard';
import { CategoryTargetCard } from './targetConditions/CategoryTargetCard';
import { PayeeTargetCard } from './targetConditions/PayeeTargetCard';

import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { transactionRulesSelectors } from 'app-components/flux/transaction-rules/transactionRulesSlice';
import { tracker } from 'app-components/utils/core';
import Dump from 'components/Dump';
import { useAppSelector } from 'hooks/redux';
import { makeStyles } from 'tss-react/mui';
import { AccountSourceCard } from './sourceConditions/AccountSourceCard';
import { AmountSourceCard } from './sourceConditions/AmountSourceCard';
import { ExcludedTargetCard } from './targetConditions/ExcludedTargetCard';
import { MemoTargetCard } from './targetConditions/MemoTargetCard';
import { ReviewedTargetCard } from './targetConditions/ReviewedTargetCard';
import { TagsTargetCard } from './targetConditions/TagsTargetCard';

const useStyles = makeStyles()((theme) => ({
  main: {
    display: 'flex',
    flex: 1,
    borderBottomRightRadius: 0,
    borderBottomLeftRadius: 0,
  },
  conditionsContainer: {
    display: 'flex',
    flex: 1,
    flexDirection: 'row',
    '> div+div': {
      borderLeft: `1px solid ${theme.palette.divider}`,
    },
  },
  conditions: {
    display: 'flex',
    flex: 1,
    padding: theme.spacing(2),
    fontWeight: 500,
    flexDirection: 'column',
    overflow: 'auto',
  },
  cardColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
}));

type RuleEditorLeftPaneProps = {
  advanceRuleConditions: boolean;
  classes: ReturnType<typeof useStyles>['classes'];
  state: ContextType['state'];
};

const RuleEditorLeftPane = ({ advanceRuleConditions, classes, state }: RuleEditorLeftPaneProps): JSX.Element => {
  return (
    <Box className={classes.conditions}>
      <Dump obj={state} />
      <SourceHeading />
      <Box className={classes.cardColumn}>
        <PayeeSourceCard />
        {advanceRuleConditions && <AccountSourceCard />}
        {advanceRuleConditions && <AmountSourceCard />}
      </Box>
    </Box>
  );
};

type RuleEditorRightPaneProps = {
  advanceRuleConditions: boolean;
  classes: ReturnType<typeof useStyles>['classes'];
  fromTxn?: boolean;
  editField?: string;
  isEditing: boolean;
};

const RuleEditorRightPane = ({
  advanceRuleConditions,
  classes,
  fromTxn,
  editField,
  isEditing,
}: RuleEditorRightPaneProps): JSX.Element => {
  return (
    <Box className={classes.conditions}>
      <TargetHeading />
      <Box className={classes.cardColumn}>
        <PayeeTargetCard hideToggle={fromTxn && editField === 'payeeTarget'} />
        <CategoryTargetCard hideToggle={fromTxn && editField === 'coaTarget'} isEditing={isEditing} />
        {advanceRuleConditions && (
          <TagsTargetCard hideToggle={fromTxn && editField === 'tagsTarget'} isEditing={isEditing} />
        )}
        {advanceRuleConditions && (
          <MemoTargetCard hideToggle={fromTxn && editField === 'tagsTarget'} isEditing={isEditing} />
        )}
        {advanceRuleConditions && (
          <ExcludedTargetCard hideToggle={fromTxn && editField === 'tagsTarget'} isEditing={isEditing} />
        )}
        {advanceRuleConditions && (
          <ReviewedTargetCard hideToggle={fromTxn && editField === 'reviewedTarget'} isEditing={isEditing} />
        )}
      </Box>
    </Box>
  );
};

const RuleEditorPanels = (): JSX.Element => {
  const { state, dispatch } = useStateContext();
  const manager = useManager();
  const { duplicateId, fromTxn, stage, editField } = state;
  const { fireAction } = manager;
  const isEditing = state.rule?.id ? true : false;

  const { classes } = useStyles();
  const originalRule = useAppSelector((state) => transactionRulesSelectors.selectById(state, duplicateId || ''));
  const advanceRuleConditions = Boolean(
    useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'txnRulesAdvanced')),
  );

  const handleDialogAction = useCallback(
    (value: ResolutionTypeEnum) => {
      tracker.track(tracker.events.txnRuleDuplicate, { userAction: value });

      switch (value) {
        case ResolutionTypeEnum.CLOSE:
          dispatch(updateDuplicateIdAction(undefined));
          dispatch(updateStageAction(EDITING));
          break;
        case ResolutionTypeEnum.EDIT:
          manager.reinit(originalRule);
          dispatch(updateDuplicateIdAction(undefined));
          dispatch(updateRuleAction(originalRule));
          dispatch(updateStageAction(EDITING));
          break;
        case ResolutionTypeEnum.REPLACE:
          dispatch(updateStageAction(VALIDATED));
          break;
        default:
          break;
      }
    },
    [dispatch, manager, originalRule],
  );

  // creating a rule from a specific transaction where the user changed the target category, so pre-fill the target category in the rule and disabled the payee condition
  if (fromTxn && editField == 'coaTarget') {
    const value = (state.rule as RemoteTransactionRuleType)?.coaTargetCondition || state.rule?.coaTarget;
    fireAction('setCondition', { condition: ConditionsEnum.COA_TARGET_CONDITION, value });

    const businessValue =
      (state.rule as RemoteTransactionRuleType)?.businessCoaTargetCondition || state.rule?.businessCoaTarget;
    fireAction('setCondition', { condition: ConditionsEnum.BUSINESS_COA_TARGET_CONDITION, value: businessValue });
    fireAction('setCondition', { condition: ConditionsEnum.PAYEE_TARGET });
  }

  if (fromTxn && state.enabledOverrides?.reviewedTarget && state.rule?.reviewedTarget !== undefined) {
    fireAction('setCondition', { condition: ConditionsEnum.IS_REVIEWED_TARGET, value: state.rule.reviewedTarget });
  }

  return (
    <Card className={classes.main}>
      <Box className={classes.conditionsContainer}>
        <RuleEditorLeftPane advanceRuleConditions={advanceRuleConditions} classes={classes} state={state} />
        <RuleEditorRightPane
          advanceRuleConditions={advanceRuleConditions}
          classes={classes}
          fromTxn={fromTxn}
          editField={editField}
          isEditing={isEditing}
        />
        {duplicateId && stage !== VALIDATED && (
          <RuleDuplciateDialog onAction={handleDialogAction} originalRule={originalRule} />
        )}
      </Box>
    </Card>
  );
};

export default RuleEditorPanels;
